import DateInput from "app/uicomponents/microcomponents/DateInput";
import { OverlayPanel } from "primereact/overlaypanel";
import { Checkbox } from "primereact/checkbox";
import { Toast } from "primereact/toast";
import { Tooltip } from "primereact/tooltip";
import React, { useEffect, useRef, useState } from "react";
import ReactQuill, { Quill } from "react-quill";
import ActivityRecord from "../../assets/svg/activity-record.svg";
import BillingHistory from "../../assets/svg/billing-history.svg";
import NoteEdit from "../../assets/svg/note-edit.svg";
import ThreadFilter from "../../assets/svg/thread-filter.svg";

import { makeRequest } from "app/utils/makeRequest";
import { getObjectFromSessionStore, getSessionStore } from "app/utils/sessionStore";
import { CONTENT_TYPE } from "app/utils/types";
import { Button } from "primereact/button";
import "react-quill/dist/quill.snow.css";
import ProfileSVG from "../../assets/profileAvatar.svg";
import HexagonThreadIcon from "app/assets/IconComponent/HexagonThreadIcon";
import { HexagonImage } from "app/uicomponents/macrocomponents/HexagonImage";

// Custom Mention Blot to store user ID and name
const Embed = Quill.import("blots/embed") as any;

class MentionBlot extends Embed {
  static blotName = "mention";

  static tagName = "span";

  static className = "mention";

  static create(data: { id: string; name: string; email?: string }) {
    const node = super.create() as HTMLElement;
    node.setAttribute("data-mention-id", String(data.id));
    node.setAttribute("data-mention-name", data.name);
    if (data.email) {
      node.setAttribute("data-mention-email", data.email);
    }
    node.setAttribute("contenteditable", "false");
    node.textContent = `@${data.name}`;
    node.style.color = "#0066cc";
    node.style.backgroundColor = "#e6f2ff";
    node.style.padding = "2px 4px";
    node.style.borderRadius = "3px";
    node.style.fontWeight = "500";
    node.style.display = "inline-block";
    node.style.cursor = "default";
    return node;
  }

  static value(node: HTMLElement) {
    return {
      id: node.getAttribute("data-mention-id") || "",
      name: node.getAttribute("data-mention-name") || "",
      email: node.getAttribute("data-mention-email") || "",
    };
  }
}

Quill.register({ "blots/mention": MentionBlot });

interface PremiumFinanceThreadEditorProps {
  isAdvisorView?: boolean;
  contentTypeFilter: CONTENT_TYPE[];
  setContentTypeFilter: (filter: CONTENT_TYPE) => void;
  onThreadCreated?: () => void;
  onFiltersApplied?: (filters: {
    category: string[] | null;
    relatedPerson: string[] | null;
    dateRange: { from: Date | null; to: Date | null };
  }) => Promise<void>;
  isDarkTheme?: boolean;
  proposalId: string;
  isReadOnly?: boolean;
  isPlan?: boolean;
  threadType?: string;
  isPolicy?: boolean;
  policyId?: string;
}

const PremiumFinanceThreadEditor: React.FC<PremiumFinanceThreadEditorProps> = ({
  isAdvisorView = false,
  contentTypeFilter,
  setContentTypeFilter,
  onThreadCreated,
  onFiltersApplied,
  isDarkTheme,
  proposalId,
  isReadOnly = false,
  isPlan = false,
  threadType = "PENDING",
  isPolicy = false,
  policyId = "",
}: PremiumFinanceThreadEditorProps) => {
  const [editorValue, setEditorValue] = useState("");
  const [activeFormats, setActiveFormats] = useState({
    bold: false,
    italic: false,
    underline: false,
  });
  const [showMentionList, setShowMentionList] = useState(false);
  const [mentionSearchTerm, setMentionSearchTerm] = useState("");
  const [mentionPosition, setMentionPosition] = useState({ top: 0, left: 0 });
  const [mentionedUserIds, setMentionedUserIds] = useState<string[]>([]);
  const [mentions, setMentions] = useState<any[]>([]);
  const [isLoadingMentions, setIsLoadingMentions] = useState(false);
  const [attachedFiles, setAttachedFiles] = useState<Array<{ file: File; id: string }>>([]);
  const [isDragging, setIsDragging] = useState(false);
  const quillRef = useRef<ReactQuill>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const toast = useRef<Toast>(null);
  const [isLoadingSubmit, setIsLoadingSubmit] = useState(false);
  const [characterCount, setCharacterCount] = useState(0);
  const maxCharacterCount = 1000;
  const filteredMentions = mentions;
  const lookUpResponse = getObjectFromSessionStore("lookUpResponse");

  // Filter states
  const filterOverlayRef = useRef<OverlayPanel>(null);
  const [selectedCategories, setSelectedCategories] = useState<string[]>([]);
  const [selectedRelatedPersons, setSelectedRelatedPersons] = useState<string[]>([]);
  const [fromDate, setFromDate] = useState<Date | null>(null);
  const [toDate, setToDate] = useState<Date | null>(null);
  const [expandedFilter, setExpandedFilter] = useState<string | null>(null);

  // Get logged-in user's profile picture
  const userProfilePicture =
    lookUpResponse?.profilePicture ||
    lookUpResponse?.profile?.profilePicture ||
    (lookUpResponse?.firstName || lookUpResponse?.lastName
      ? `https://ui-avatars.com/api/?name=${encodeURIComponent(
          `${lookUpResponse?.firstName || ""} ${lookUpResponse?.lastName || ""}`,
        )}&background=random`
      : ProfileSVG);

  const modules = {
    toolbar: false, // Disable built-in toolbar
  };

  const formats = ["bold", "italic", "underline", "mention"];

  const updateActiveFormats = () => {
    const editor = quillRef.current?.getEditor();
    if (editor) {
      const format = editor.getFormat();
      setActiveFormats({
        bold: Boolean(format.bold),
        italic: Boolean(format.italic),
        underline: Boolean(format.underline),
      });
    }
  };

  const getMentionedUserIds = async () => {
    // Get mention list from API
    setIsLoadingMentions(true);
    try {
      const query = `name=${mentionSearchTerm}&${isPlan ? "planId" : "proposalId"}=${proposalId}&${
        isPolicy ? `policyId=${policyId}` : ""
      }`;
      const response = await makeRequest("pfThreadMentionsSearch").get(query, false, true);
      const data = response?.data?.data || [];
      setMentions(data);
    } catch (error) {
      setMentions([]);
    } finally {
      setIsLoadingMentions(false);
    }
  };

  useEffect(() => {
    // Debounce API call
    const timeoutId = setTimeout(() => {
      if (showMentionList) {
        getMentionedUserIds();
      }
    }, 300);

    return () => clearTimeout(timeoutId);
  }, [mentionSearchTerm, showMentionList]);

  // Extract mentioned user IDs from editor content
  const extractMentionedUserIds = () => {
    const editor = quillRef.current?.getEditor();
    if (!editor) return [];

    const contents = editor.getContents();
    const mentionedIds: string[] = [];

    contents.ops?.forEach((op: any) => {
      if (op.insert?.mention) {
        const id = typeof op.insert.mention === "object" ? op.insert.mention.id : op.insert.mention;
        const stringId = String(id);
        if (stringId && !mentionedIds.includes(stringId)) {
          mentionedIds.push(stringId);
        }
      }
    });

    return mentionedIds;
  };

  useEffect(() => {
    const ids = extractMentionedUserIds();
    setMentionedUserIds(ids);
  }, [editorValue]);

  const checkForMentions = () => {
    const editor = quillRef.current?.getEditor();
    if (!editor) return;

    const selection = editor.getSelection();
    if (!selection) return;

    const textBeforeCursor = editor.getText(0, selection.index);
    const mentionMatch = textBeforeCursor.match(/@(\w*)$/);

    if (mentionMatch) {
      const searchTerm = mentionMatch[1];
      setMentionSearchTerm(searchTerm);

      // Get cursor position for mention list positioning
      const bounds = editor.getBounds(selection.index);
      if (bounds) {
        // Get the editor container's position relative to the viewport
        const editorContainer = (quillRef.current?.getEditor() as any)?.container;
        const containerRect = editorContainer?.getBoundingClientRect();

        if (containerRect) {
          // Calculate position relative to the viewport
          const top = containerRect.top + bounds.top + bounds.height + 5;
          const left = containerRect.left + bounds.left;

          setMentionPosition({
            top,
            left,
          });
        }
      }

      setShowMentionList(true);
    } else {
      setShowMentionList(false);
      setMentionSearchTerm("");
    }
  };

  const handleChange = (value: string) => {
    const editor = quillRef.current?.getEditor();
    if (!editor) return;

    // Get plain text content (strips HTML tags)
    const plainText = editor.getText().trim();
    const textLength = plainText.length;

    // Update character count (always show exact count, even if exceeds limit)
    setCharacterCount(textLength);

    setEditorValue(value);
    // Small delay to ensure format detection works properly
    setTimeout(updateActiveFormats, 10);

    // Check for @ mentions
    checkForMentions();
  };

  const insertMention = (mention: { id: string; name: string; email?: string }) => {
    const editor = quillRef.current?.getEditor();
    if (!editor) return;

    const selection = editor.getSelection();
    if (!selection) return;

    const textBeforeCursor = editor.getText(0, selection.index);
    const mentionMatch = textBeforeCursor.match(/@(\w*)$/);

    if (mentionMatch) {
      const startIndex = selection.index - mentionMatch[0].length;
      // Delete the @ and search text
      editor.deleteText(startIndex, mentionMatch[0].length);

      // Insert the mention using the custom blot (this stores the ID)
      editor.insertEmbed(startIndex, "mention", {
        id: mention.id,
        name: mention.name,
        email: mention.email,
      });

      // Insert a space after the mention
      editor.insertText(startIndex + 1, " ");

      // Set cursor position after the inserted mention and space
      setTimeout(() => {
        editor.setSelection(startIndex + 2, 0);
      }, 10);
    }

    setShowMentionList(false);
    setMentionSearchTerm("");
  };

  const onSubmit = async () => {
    // Validate character count before submitting
    if (characterCount > maxCharacterCount) {
      toast.current?.show({
        severity: "warn",
        summary: "Character Limit Exceeded",
        detail: `Please reduce your text to ${maxCharacterCount} characters or less. Current: ${characterCount} characters.`,
        life: 3000,
      });
      return;
    }

    setIsLoadingSubmit(true);
    try {
      // Create FormData for multipart/form-data
      const formData = new FormData();

      // Add note (HTML content from editor)
      formData.append("note", editorValue);

      if (threadType === "REVIEW") {
        const planYear = getSessionStore("ircSelectedYear");
        if (planYear != null) formData.append("planYear", String(planYear));
      }

      // Add threadType (you can change this based on your needs)
      formData.append("threadType", threadType);

      // Add proposalId (you should replace this with the actual proposalId from props/state)
      formData.append(isPlan ? "planId" : "proposalId", proposalId);

      if (isPolicy) {
        formData.append("policyId", policyId);
      }

      // Add mentions (array of user IDs) - using bracket notation for array
      if (mentionedUserIds.length > 0) {
        mentionedUserIds.forEach((mentionId) => {
          formData.append("mentions[]", mentionId);
        });
      }

      // Add attachments (files)
      if (attachedFiles.length > 0) {
        attachedFiles.forEach((attachedFile) => {
          formData.append("attachments", attachedFile.file);
        });
      }

      // Make the API request
      const response = await makeRequest("pfthreads", {
        "Content-Type": "multipart/form-data",
      }).post(formData);

      // Show success toast
      toast.current?.show({
        severity: "success",
        summary: "Success",
        detail: response.data?.message || "Thread created successfully!",
        life: 3000,
      });

      // Clear the editor and attachments after successful submission
      setEditorValue("");
      setAttachedFiles([]);
      setMentionedUserIds([]);
      setCharacterCount(0);

      // Call the callback to refresh timeline data
      if (onThreadCreated) {
        onThreadCreated();
      }
    } catch (error) {
      // Show error toast
      toast.current?.show({
        severity: "error",
        summary: "Error",
        detail: "Failed to create thread. Please try again.",
        life: 3000,
      });
    } finally {
      setIsLoadingSubmit(false);
    }
  };

  const handleKeyDown = (event: any) => {
    if (showMentionList && event.key === "Escape") {
      setShowMentionList(false);
    }
  };

  // File validation functions
  const validateFileSize = (file: File): boolean => {
    const maxSize = 25 * 1024 * 1024; // 25 MB in bytes
    return file.size <= maxSize;
  };

  const validateFileFormat = (file: File): boolean => {
    const allowedExtensions = [".pdf", ".doc", ".docx", ".xls", ".xlsx", ".zip"];
    const fileName = file.name.toLowerCase();
    return allowedExtensions.some((ext) => fileName.endsWith(ext));
  };

  const validateFiles = (files: File[]): { validFiles: File[]; errors: string[] } => {
    const validFiles: File[] = [];
    const errors: string[] = [];

    files.forEach((file) => {
      // Check file size
      if (!validateFileSize(file)) {
        errors.push(`${file.name}: Please upload file up to 25 MB.`);
        return;
      }

      // Check file format
      if (!validateFileFormat(file)) {
        errors.push(`${file.name}: Please upload file in ZIP/PDF/Word Document/Excel format.`);
        return;
      }

      validFiles.push(file);
    });

    return { validFiles, errors };
  };

  // File upload handlers
  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { files } = event.target;
    if (files) {
      const maxFiles = 5;
      const remainingSlots = maxFiles - attachedFiles.length;

      if (remainingSlots <= 0) {
        toast.current?.show({
          severity: "warn",
          summary: "Maximum Files Reached",
          detail: `You can only attach up to ${maxFiles} files.`,
          life: 3000,
        });
        if (event.target) {
          event.target.value = "";
        }
        return;
      }

      const filesToAdd = Array.from(files).slice(0, remainingSlots);

      // Validate files
      const { validFiles, errors } = validateFiles(filesToAdd);

      // Show errors if any
      if (errors.length > 0) {
        errors.forEach((error) => {
          toast.current?.show({
            severity: "error",
            summary: "File Validation Error",
            detail: error,
            life: 4000,
          });
        });
      }

      // Add valid files
      if (validFiles.length > 0) {
        const newFiles = validFiles.map((file) => ({
          file,
          id: `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
        }));
        setAttachedFiles((prev) => [...prev, ...newFiles]);
      }

      if (files.length > remainingSlots) {
        toast.current?.show({
          severity: "warn",
          summary: "File Limit",
          detail: `Only ${remainingSlots} file(s) can be added. Maximum ${maxFiles} files allowed.`,
          life: 3000,
        });
      }
    }
    // Reset input value to allow selecting the same file again
    if (event.target) {
      event.target.value = "";
    }
  };

  const handleFileRemove = (fileId: string) => {
    setAttachedFiles((prev) => prev.filter((f) => f.id !== fileId));
  };

  const handleDragOver = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    event.stopPropagation();
    setIsDragging(true);
  };

  const handleDragLeave = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    event.stopPropagation();
    setIsDragging(false);
  };

  const handleDrop = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    event.stopPropagation();
    setIsDragging(false);

    const { files } = event.dataTransfer;
    if (files) {
      const maxFiles = 5;
      const remainingSlots = maxFiles - attachedFiles.length;

      if (remainingSlots <= 0) {
        toast.current?.show({
          severity: "warn",
          summary: "Maximum Files Reached",
          detail: `You can only attach up to ${maxFiles} files.`,
          life: 3000,
        });
        return;
      }

      const filesToAdd = Array.from(files).slice(0, remainingSlots);

      // Validate files
      const { validFiles, errors } = validateFiles(filesToAdd);

      // Show errors if any
      if (errors.length > 0) {
        errors.forEach((error) => {
          toast.current?.show({
            severity: "error",
            summary: "File Validation Error",
            detail: error,
            life: 4000,
          });
        });
      }

      // Add valid files
      if (validFiles.length > 0) {
        const newFiles = validFiles.map((file) => ({
          file,
          id: `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
        }));
        setAttachedFiles((prev) => [...prev, ...newFiles]);
      }

      if (files.length > remainingSlots) {
        toast.current?.show({
          severity: "warn",
          summary: "File Limit",
          detail: `Only ${remainingSlots} file(s) can be added. Maximum ${maxFiles} files allowed.`,
          life: 3000,
        });
      }
    }
  };

  const formatFileSize = (bytes: number): string => {
    if (bytes === 0) return "0 Bytes";
    const k = 1024;
    const sizes = ["Bytes", "KB", "MB", "GB"];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return `${Math.round((bytes / k ** i) * 100) / 100} ${sizes[i]}`;
  };

  // Filter toggle handlers
  const toggleCategoryFilter = () => {
    setExpandedFilter(expandedFilter === "category" ? null : "category");
  };

  const toggleRelatedPersonFilter = () => {
    setExpandedFilter(expandedFilter === "relatedPerson" ? null : "relatedPerson");
  };

  const toggleDateRangeFilter = () => {
    setExpandedFilter(expandedFilter === "dateRange" ? null : "dateRange");
  };

  const clearFilters = async () => {
    // Clear filter states
    setSelectedCategories([]);
    setSelectedRelatedPersons([]);
    setFromDate(null);
    setToDate(null);

    // Apply cleared filters (make API call with empty filters)
    const clearedFilters = {
      category: null,
      relatedPerson: null,
      dateRange: { from: null, to: null },
    };

    // Pass cleared filters to parent component and wait for API call to complete
    if (onFiltersApplied) {
      await onFiltersApplied(clearedFilters);
    }

    filterOverlayRef.current?.hide();
  };

  const applyFilters = async () => {
    // Handle filter application logic here
    const filters = {
      category: selectedCategories.length > 0 ? selectedCategories : null,
      relatedPerson: selectedRelatedPersons.length > 0 ? selectedRelatedPersons : null,
      dateRange: { from: fromDate, to: toDate },
    };

    // Pass filters to parent component and wait for API call to complete
    if (onFiltersApplied) {
      await onFiltersApplied(filters);
    }

    filterOverlayRef.current?.hide();
  };

  return (
    <>
      <style>
        {`
          .ql-editor {
            word-wrap: break-word;
            word-break: break-word;
            overflow-wrap: break-word;
            white-space: pre-wrap;
            max-width: 100%;
            overflow-x: hidden !important;
          }
          .ql-container {
            max-width: 100%;
            overflow-x: hidden !important;
          }
        `}
      </style>
      <Toast ref={toast} />

      {/* Filter Overlay Panel */}
      <OverlayPanel ref={filterOverlayRef} style={{ width: "350px" }}>
        <div className="flex flex-col gap-4 p-2">
          {/* Category Filter */}
          {threadType !== "REVIEW" && (
            <div className="border-b pb-3">
              <div
                className="flex justify-between items-center cursor-pointer"
                onClick={toggleCategoryFilter}
              >
                <h3
                  className={`text-base font-semibold ${
                    isDarkTheme ? "text-white" : "text-gray-800"
                  }`}
                >
                  Category
                </h3>
                <i
                  className={`pi ${
                    expandedFilter === "category" ? "pi-chevron-up" : "pi-chevron-down"
                  } ${isDarkTheme ? "text-white" : "text-gray-600"}`}
                />
              </div>
              {expandedFilter === "category" && (
                <div className="flex flex-col gap-2 mt-3 ml-2">
                  {["Plans", "Financing", "Pending", "Policies", "Proposal"].map((category) => (
                    <div key={category} className="flex align-items-center">
                      <Checkbox
                        inputId={`category-${category}`}
                        name="categories"
                        value={category}
                        onChange={(e) => {
                          let next = [...selectedCategories];
                          if (e.checked) {
                            if (!next.includes(category)) next.push(category);
                          } else {
                            next = next.filter((c) => c !== category);
                          }
                          setSelectedCategories(next);
                        }}
                        checked={selectedCategories.includes(category)}
                      />
                      <label
                        htmlFor={`category-${category}`}
                        className={`ml-2 cursor-pointer ${
                          isDarkTheme ? "text-white" : "text-gray-700"
                        }`}
                      >
                        {category}
                      </label>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Related Person Filter */}
          <div className="border-b pb-3">
            <div
              className="flex justify-between items-center cursor-pointer"
              onClick={toggleRelatedPersonFilter}
            >
              <h3
                className={`text-base font-semibold ${
                  isDarkTheme ? "text-white" : "text-gray-800"
                }`}
              >
                Related Person
              </h3>
              <i
                className={`pi ${
                  expandedFilter === "relatedPerson" ? "pi-chevron-up" : "pi-chevron-down"
                } ${isDarkTheme ? "text-white" : "text-gray-600"}`}
              />
            </div>
            {expandedFilter === "relatedPerson" && (
              <div className="flex flex-col gap-2 mt-3 ml-2">
                {["ADMIN", "ADVISOR", "IAPP", "CONTACT"].map((person) => {
                  // Display name mapping - show different names in UI but keep same values
                  const personDisplayNames: { [key: string]: string } = {
                    ADMIN: "IAPP ADMIN",
                    ADVISOR: "ADVISOR",
                    IAPP: "IAPP SYSTEM GENERATED",
                    CONTACT: "CONTACT",
                  };
                  const displayName = personDisplayNames[person] || person;
                  return (
                    <div key={person} className="flex align-items-center">
                      <Checkbox
                        inputId={`person-${person}`}
                        name="relatedPersons"
                        value={person}
                        onChange={(e) => {
                          let next = [...selectedRelatedPersons];
                          if (e.checked) {
                            if (!next.includes(person)) next.push(person);
                          } else {
                            next = next.filter((p) => p !== person);
                          }
                          setSelectedRelatedPersons(next);
                        }}
                        checked={selectedRelatedPersons.includes(person)}
                      />
                      <label
                        htmlFor={`person-${person}`}
                        className={`ml-2 cursor-pointer ${
                          isDarkTheme ? "text-white" : "text-gray-700"
                        }`}
                      >
                        {displayName}
                      </label>
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          {/* Date Range Filter */}
          <div className="pb-2">
            <div
              className="flex justify-between items-center cursor-pointer"
              onClick={toggleDateRangeFilter}
            >
              <h3
                className={`text-base font-semibold ${
                  isDarkTheme ? "text-white" : "text-gray-800"
                }`}
              >
                Date Range
              </h3>
              <i
                className={`pi ${
                  expandedFilter === "dateRange" ? "pi-chevron-up" : "pi-chevron-down"
                } ${isDarkTheme ? "text-white" : "text-gray-600"}`}
              />
            </div>
            {expandedFilter === "dateRange" && (
              <div className="flex flex-col gap-3 mt-3 relative">
                <div className="flex flex-col gap-1 relative">
                  <label
                    htmlFor="fromDate"
                    className={`text-sm font-medium ${
                      isDarkTheme ? "text-white" : "text-gray-700"
                    }`}
                  >
                    From Date
                  </label>
                  <div className="relative">
                    <DateInput
                      id="fromDate"
                      value={fromDate}
                      onChange={(date) => setFromDate(date)}
                      placeholder="Select start date"
                      className="w-full"
                      maxDate={toDate || undefined}
                      bgColor={isDarkTheme ? "dark:!bg-[#040d19]" : "bg-white"}
                      bgInputColor={isDarkTheme ? "dark:!bg-[#040d19]" : "bg-gray-400"}
                      inputClassName="!p-3"
                    />
                  </div>
                </div>
                <div className="flex flex-col gap-1 relative">
                  <label
                    htmlFor="toDate"
                    className={`text-sm font-medium ${
                      isDarkTheme ? "text-white" : "text-gray-700"
                    }`}
                  >
                    To Date
                  </label>
                  <div className="relative">
                    <DateInput
                      id="toDate"
                      value={toDate}
                      onChange={(date) => setToDate(date)}
                      placeholder="Select end date"
                      className="w-full"
                      minDate={fromDate || new Date()}
                      disabled={!fromDate}
                      bgColor={isDarkTheme ? "dark:!bg-[#040d19]" : "bg-white"}
                      bgInputColor={isDarkTheme ? "dark:!bg-[#040d19]" : "bg-gray-400"}
                      inputClassName="!p-3"
                    />
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Action Buttons */}
          <div className="flex gap-2 justify-end pt-2 border-t">
            <Button
              label="Clear"
              onClick={clearFilters}
              className="p-button-text p-button-sm"
              size="small"
            />
            <Button label="Apply" onClick={applyFilters} className="p-button-sm" size="small" />
          </div>
        </div>
      </OverlayPanel>

      <div className="flex flex-row items-center">
        <div className="flex flex-row  mb-2 gap-2 items-center justify-center">
          <HexagonThreadIcon width={50} height={50} />
          <span className="w-full h-full mt-1 font-semibold text-xl pl-1 text-[#191A1C] dark:text-white">
            Thread
          </span>
        </div>
        <div className="ml-auto flex flex-row gap-2 mr-4 items-center">
          <Tooltip target=".note-edit-icon" content="Notes" position="bottom" />

          <img
            src={NoteEdit}
            alt="NoteEdit"
            className={`cursor-pointer note-edit-icon dark:filter dark:invert ${
              contentTypeFilter.includes(CONTENT_TYPE.NOTES)
                ? "opacity-100 border-2 border-blue-500 rounded p-1"
                : "opacity-70"
            }`}
            data-pr-tooltip="Notes"
            onClick={() => setContentTypeFilter(CONTENT_TYPE.NOTES)}
          />

          <Tooltip target=".activity-record-icon" content="Activity Record" position="bottom" />

          <img
            src={ActivityRecord}
            alt="ActivityRecord"
            className={`cursor-pointer activity-record-icon dark:filter dark:invert ${
              contentTypeFilter.includes(CONTENT_TYPE.ACTIVITY)
                ? "opacity-100 border-2 border-blue-500 rounded p-1"
                : "opacity-70"
            }`}
            data-pr-tooltip="Activity Record"
            onClick={() => setContentTypeFilter(CONTENT_TYPE.ACTIVITY)}
          />

          {threadType !== "REVIEW" && (
            <>
              <Tooltip target=".billing-history-icon" content="Billing History" position="bottom" />

              <img
                src={BillingHistory}
                alt="BillingHistory"
                className={`cursor-pointer billing-history-icon dark:filter dark:invert ${
                  contentTypeFilter.includes(CONTENT_TYPE.BILLING)
                    ? "opacity-100 border-2 border-blue-500 rounded p-1"
                    : "opacity-70"
                }`}
                data-pr-tooltip="Billing History"
                onClick={() => setContentTypeFilter(CONTENT_TYPE.BILLING)}
              />
            </>
          )}

          <Tooltip target=".thread-filter-icon" content="Thread Filter" position="bottom" />
          <img
            src={ThreadFilter}
            alt="ThreadFilter"
            className="cursor-pointer thread-filter-icon dark:filter dark:invert"
            data-pr-tooltip="Thread Filter"
            onClick={(e) => filterOverlayRef.current?.toggle(e)}
          />
        </div>
      </div>
      {!isAdvisorView && (
        <>
          <div className="p-1 font-sans flex flex-row gap-2">
            {/* Profile Picture */}
            <div className="flex items-center justify-center mb-auto mt-[2rem]">
              <HexagonImage
                imageUrl={userProfilePicture}
                alt="Profile"
                imageSize={64}
                // borderColor={primaryColor}
                borderWidth={2}
              />
            </div>

            <div className="w-full h-full mx-auto rounded-xl p-4 flex-1 max-w-full overflow-hidden">
              {/* Editor Container */}
              <div
                className="flex flex-col overflow-hidden max-w-full"
                onDragOver={handleDragOver}
                onDragLeave={handleDragLeave}
                onDrop={handleDrop}
              >
                {/* Text area */}
                <div className={`flex-1 relative ${isDragging ? "opacity-50" : ""} max-w-full`}>
                  <ReactQuill
                    ref={quillRef}
                    theme="bubble"
                    value={editorValue}
                    onChange={handleChange}
                    onKeyDown={handleKeyDown}
                    modules={modules}
                    formats={formats}
                    placeholder="Post here..."
                    readOnly={isReadOnly}
                    className={`h-6rem border-2 border-gray-300 shadow-lg rounded-lg ${
                      isDarkTheme ? "bg-gray-800 text-white" : "bg-white text-gray-900"
                    } max-w-full`}
                    style={{ width: "100%", maxWidth: "100%", overflow: "hidden" }}
                  />
                  {isDragging && (
                    <div className="absolute inset-0 border-2 border-dashed border-blue-500 bg-blue-50 bg-opacity-50 flex items-center justify-center rounded-lg z-10">
                      <p className="text-blue-600 font-semibold">Drop files here</p>
                    </div>
                  )}

                  {/* Mention List */}
                  {showMentionList && (
                    <div
                      className="fixed bg-white border border-gray-300 rounded-lg shadow-lg w-80 h-64  overflow-y-auto z-50"
                      style={{
                        top: `${mentionPosition.top}px`,
                        left: `${mentionPosition.left}px`,
                        zIndex: 9999,
                      }}
                      onMouseDown={(e) => e.preventDefault()}
                    >
                      {isLoadingMentions ? (
                        <div className="px-3 py-2 text-gray-600 text-sm flex items-center justify-center">
                          Loading...
                        </div>
                      ) : filteredMentions.length > 0 ? (
                        filteredMentions.map((mention, index) => (
                          <div
                            key={`${mention.id}-${index}`}
                            onClick={(e) => {
                              e.preventDefault();
                              e.stopPropagation();
                              insertMention(mention);
                            }}
                            onMouseDown={(e) => {
                              e.preventDefault();
                              e.stopPropagation();
                            }}
                            className="px-3 py-2 cursor-pointer border-b border-gray-100 flex items-center gap-2 bg-white hover:bg-blue-50 transition-colors"
                          >
                            {mention.profilePicture ? (
                              <img
                                src={mention.profilePicture}
                                alt={mention.name}
                                className="w-8 h-8 rounded-full object-cover"
                              />
                            ) : (
                              <div className=" w-1 h-8 rounded-full flex items-center justify-center text-white text-xs font-semibold">
                                <img
                                  src={`https://ui-avatars.com/api/?name=${mention.name}`}
                                  alt={mention.name}
                                  className="w-full h-full rounded-full object-cover"
                                />
                              </div>
                            )}
                            <div className="flex flex-col flex-1">
                              <span className="font-medium text-sm">{mention.name}</span>
                              <span className="text-xs text-gray-600">{mention.email}</span>
                            </div>
                            <span className="text-xs text-gray-400 capitalize">
                              {mention.userType}
                            </span>
                          </div>
                        ))
                      ) : (
                        <div className="px-3 py-2 text-gray-600 text-sm">
                          {mentionSearchTerm ? "No mentions found" : "Type to search..."}
                        </div>
                      )}
                    </div>
                  )}
                </div>

                {/* Character Counter */}
                <div className="flex justify-end pt-2">
                  <span
                    className={`text-xs font-medium ${
                      characterCount > maxCharacterCount * 0.9
                        ? "text-red-600"
                        : characterCount > maxCharacterCount * 0.7
                        ? "text-yellow-600"
                        : "text-gray-500 dark:text-gray-400"
                    }`}
                  >
                    {characterCount}/{maxCharacterCount} characters
                  </span>
                </div>

                {/* Custom toolbar at bottom */}
                <div className=" pt-3 border-t border-gray-200 flex items-center justify-between">
                  <div className="flex gap-4 items-center w-full">
                    <button
                      type="button"
                      onClick={() => {
                        if (isReadOnly) {
                          return;
                        }
                        const editor = quillRef.current?.getEditor();
                        if (editor) {
                          editor.format("bold", !activeFormats.bold);
                          setActiveFormats((prev) => ({ ...prev, bold: !prev.bold }));
                        }
                      }}
                      className={`p-1 ${
                        !isReadOnly ? "cursor-pointer" : "cursor-not-allowed"
                      } font-bold text-sm rounded transition-all duration-200 ${
                        activeFormats.bold
                          ? "text-blue-600"
                          : isDarkTheme
                          ? "text-white"
                          : "text-gray-500 hover:text-gray-700"
                      }`}
                    >
                      B
                    </button>
                    <button
                      type="button"
                      onClick={() => {
                        if (isReadOnly) {
                          return;
                        }
                        const editor = quillRef.current?.getEditor();
                        if (editor) {
                          editor.format("italic", !activeFormats.italic);
                          setActiveFormats((prev) => ({ ...prev, italic: !prev.italic }));
                        }
                      }}
                      className={`p-1 ${
                        !isReadOnly ? "cursor-pointer" : "cursor-not-allowed"
                      } italic text-sm rounded transition-all duration-200 ${
                        activeFormats.italic
                          ? "text-blue-600"
                          : isDarkTheme
                          ? "text-white"
                          : "text-gray-500 hover:text-gray-700"
                      }`}
                    >
                      I
                    </button>
                    <button
                      type="button"
                      onClick={() => {
                        if (isReadOnly) {
                          return;
                        }
                        const editor = quillRef.current?.getEditor();
                        if (editor) {
                          editor.format("underline", !activeFormats.underline);
                          setActiveFormats((prev) => ({ ...prev, underline: !prev.underline }));
                        }
                      }}
                      className={`p-1 ${
                        !isReadOnly ? "cursor-pointer" : "cursor-not-allowed"
                      } underline text-sm rounded transition-all duration-200 ${
                        activeFormats.underline
                          ? "text-blue-600"
                          : isDarkTheme
                          ? "text-white"
                          : "text-gray-500 hover:text-gray-700"
                      }`}
                    >
                      U
                    </button>
                    <button
                      type="button"
                      onClick={() => {
                        if (isReadOnly) {
                          return;
                        }
                        const editor = quillRef.current?.getEditor();
                        if (editor) {
                          const selection = editor.getSelection();
                          const cursorPosition = selection ? selection.index : editor.getLength();

                          // Insert "@" at cursor position
                          editor.insertText(cursorPosition, "@");

                          // Move cursor after the "@"
                          editor.setSelection(cursorPosition + 1, 0);

                          // Trigger the mention feature
                          setTimeout(() => {
                            checkForMentions();
                          }, 10);
                        }
                      }}
                      className={`p-1 ${
                        !isReadOnly ? "cursor-pointer" : "cursor-not-allowed"
                      } text-sm rounded transition-all duration-200 ${
                        isDarkTheme ? "text-white" : "text-gray-500 hover:text-gray-700"
                      }`}
                    >
                      @
                    </button>
                    <div className={`text-sm ${isDarkTheme ? "text-white" : "text-gray-500"}`}>
                      Attach files by dragging & dropping or{" "}
                      <span
                        className={`text-blue-600 ${!isReadOnly && "cursor-pointer"} underline`}
                        onClick={() => {
                          if (isReadOnly) {
                            return;
                          }
                          fileInputRef.current?.click();
                        }}
                      >
                        Select a file
                      </span>
                    </div>
                    <input
                      ref={fileInputRef}
                      type="file"
                      multiple
                      onChange={handleFileSelect}
                      className="hidden"
                    />

                    {/* Submit Button */}
                    <div className="flex justify-end  ml-auto">
                      <Button
                        type="button"
                        onClick={onSubmit}
                        data-pr-tooltip="Submit"
                        loading={isLoadingSubmit}
                        className="px-6 py-1 cursor-pointer bg-blue-600 hover:bg-blue-700 text-white font-medium rounded-lg shadow-md transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
                        disabled={characterCount === 0 || isReadOnly}
                      >
                        Post
                      </Button>
                    </div>
                  </div>
                </div>
              </div>

              {/* Attached Files Display */}
              {attachedFiles.length > 0 && (
                <div className="flex flex-row flex-wrap gap-2 mt-4">
                  {attachedFiles.map((attachedFile) => (
                    <div
                      key={attachedFile.id}
                      className="flex h-12 w-64 items-center bg-gray-100 hover:bg-gray-200 px-3 py-2 rounded-lg border border-gray-300 transition-colors"
                    >
                      <div className="flex items-center gap-2 flex-1 min-w-0">
                        {/* File Icon */}
                        <i className="pi pi-file text-2xl text-start text-gray-600 flex-shrink-0" />

                        {/* File Info */}
                        <div className="flex flex-col min-w-0">
                          <span className="text-sm font-medium text-gray-800 truncate">
                            {attachedFile.file.name}
                          </span>
                          <span className="text-xs text-gray-500">
                            {formatFileSize(attachedFile.file.size)}
                          </span>
                        </div>
                      </div>

                      <button
                        type="button"
                        className="ml-auto p-1 hover:bg-red-100 rounded-full transition-colors flex-shrink-0"
                        onClick={() => handleFileRemove(attachedFile.id)}
                        aria-label="Remove file"
                      >
                        <i className="pi pi-times text-red-600 p-1" style={{ fontSize: "1rem" }} />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </>
      )}
    </>
  );
};

export default PremiumFinanceThreadEditor;
